import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import {
  PERIOD_REPORT_QUERY_PARAM,
  RECEIPT_QUERY_PARAM,
  ZREPORT_QUERY_PARAM,
} from "../../cash-register.constants";
import { IUseRegisterModals } from "./use-register-modals.models";

/**
 * Which of the three modals is open lives in the URL, not component state —
 * `?receipt=`/`?zReport=`/`?periodReport=` make a specific receipt or
 * Z-report linkable, and let the register page open straight into it.
 * Opening one clears the others (only one renders at a time); a hand-built
 * URL carrying more than one resolves receipt > zReport > periodReport.
 */
export const useRegisterModals = (): IUseRegisterModals => {
  const [searchParams, setSearchParams] = useSearchParams();

  const receiptFn = searchParams.get(RECEIPT_QUERY_PARAM);
  const zReportFn = receiptFn ? null : searchParams.get(ZREPORT_QUERY_PARAM);
  const isPeriodReportOpen =
    !receiptFn && !zReportFn && searchParams.has(PERIOD_REPORT_QUERY_PARAM);

  const openModal = useCallback(
    (param: string, value: string) => {
      const next = new URLSearchParams();
      next.set(param, value);
      setSearchParams(next);
    },
    [setSearchParams],
  );

  const closeModal = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const openReceipt = useCallback(
    (fiscalNumber: string) => openModal(RECEIPT_QUERY_PARAM, fiscalNumber),
    [openModal],
  );
  const openZReport = useCallback(
    (fiscalNumber: string) => openModal(ZREPORT_QUERY_PARAM, fiscalNumber),
    [openModal],
  );
  const openPeriodReport = useCallback(
    () => openModal(PERIOD_REPORT_QUERY_PARAM, "1"),
    [openModal],
  );

  return {
    receiptFn,
    zReportFn,
    isPeriodReportOpen,
    openReceipt,
    openZReport,
    openPeriodReport,
    closeModal,
  };
};
